import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useState, useEffect } from 'react'
import type { ApplicationDocument, ApplicationFormDocument, FieldAnswer } from '@/types'
import { applicationsService } from '../services/applications.service'
import type { ApplicationUploadReceipt } from '@/lib/firebase/functions'

import { browserAttemptStorage, createSubmissionAttemptStore } from '../lib/submissionAttempt'

interface UseApplicationFormResult {
  application: (ApplicationDocument & { id: string }) | null
  form: (ApplicationFormDocument & { id: string }) | null
  loading: boolean
  error: string | null
  submitting: boolean
  submitted: boolean
  submit: (payload: SubmitPayload) => Promise<void>
}

interface SubmitPayload {
  applicantInfo: { firstName: string; lastName: string; email: string }
  answers: Record<string, FieldAnswer>
  fileUploads: Record<string, ApplicationUploadReceipt[]>
  termsAccepted: boolean
}

export function useApplicationForm(slug: string): UseApplicationFormResult {
  const [application, setApplication] = useState<(ApplicationDocument & { id: string }) | null>(
    null
  )
  const [form, setForm] = useState<(ApplicationFormDocument & { id: string }) | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [attempts] = useState(() => createSubmissionAttemptStore(browserAttemptStorage()))
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setApplication(null)
    setForm(null)
    setSubmitted(false)

    async function load() {
      try {
        const app = await applicationsService.getBySlug(slug)
        if (cancelled) return
        if (!app) {
          setError(withErrorCode('Application not found.', 'app/validation'))
          return
        }
        setApplication(app)
        const f = await applicationsService.getFormByApplicationId(app.id)
        if (cancelled) return
        if (!f) {
          setError(withErrorCode('This application form is unavailable.', 'applications/form'))
          return
        }
        setForm(f)
      } catch (err) {
        if (cancelled) return
        setError(formatUserFacingError(err, 'Failed to load application.'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slug])

  async function submit(payload: SubmitPayload) {
    if (!application || !form) return
    setSubmitting(true)
    try {
      const scope = `${application.id}:${form.id}`
      const submissionRequestId = await attempts.get(scope, payload)
      await applicationsService.submit({
        applicationId: application.id,
        formId: form.id,
        ...payload,
        submissionRequestId,
      })
      await attempts.complete(scope, payload, submissionRequestId)
      setSubmitted(true)
    } finally {
      setSubmitting(false)
    }
  }

  return { application, form, loading, error, submitting, submitted, submit }
}
